import { Button } from 'components/tsxCommon/Button';
import { PlayButton } from 'components/tsxCommon/PlayButton';
import { getImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { trackEvent, TrackingEvents, VideoPlayClickSource } from 'helpers/TrackingHelper';
import { useClickOutside } from 'hooks/useClickOutside';
import React, { useState } from 'react';

import PlayButtonSVG from '../../../static/images/svg/deco/playButton.inline.svg';

type VideoHeroProps = {
    videoUrl: string;
    paragraph: string | JSX.Element;
    header: string | JSX.Element;
    videoViewTrackingEvent?: (typeof TrackingEvents)[keyof typeof TrackingEvents];
    poster: IGatsbyImageData;
    cta?: JSX.Element;
    title?: string;
    playButtonText?: string;
};

export const VideoHero = ({
    videoUrl,
    header,
    paragraph,
    videoViewTrackingEvent,
    poster,
    cta,
    title,
    playButtonText
}: VideoHeroProps) => {
    const [showFullScreenVideo, setShowFullScreenVideo] = useState(false);
    const ref = useClickOutside(() => setShowFullScreenVideo(false));

    return (
        <>
            {showFullScreenVideo ? (
                <div className="tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-overlay tw-z-50 tw-flex tw-items-center">
                    <div className="container">
                        <div className="tw-flex tw-flex-col tw-gap-24">
                            <div
                                className="tw-p-8 tw-rounded-[2px]"
                                style={{
                                    background:
                                        'linear-gradient(107.67deg, #3C3D3E -1.17%, #5B5C5D 28.98%, #292B2C 101.68%)',
                                    boxShadow: 'inset 2px 2px 1px rgba(255, 255, 255, 0.4)'
                                }}
                            >
                                <video
                                    ref={ref}
                                    poster={poster ? getImage(poster).images.fallback.src : null}
                                    autoPlay
                                    preload="auto"
                                    controls
                                    className="tw-w-full tw-h-full tw-aspect-video tw-max-h-[80vh]"
                                >
                                    <source src={videoUrl} type="video/mp4" />
                                    <p>
                                        Your browser doesn't support HTML5 video. Here is a
                                        <a href={videoUrl}>link to the video</a> instead.
                                    </p>
                                </video>
                            </div>
                            <div>
                                <div className="tw-flex tw-justify-center tw-shrink-0">
                                    <Button
                                        text="Close Video"
                                        variant="secondary"
                                        buttonVariant="callback"
                                        callback={() => setShowFullScreenVideo(false)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            <div className="tw-relative lg:tw-h-[60vh] lg:tw-min-h-[800px] lg:block tw-flex tw-flex-col-reverse lg:tw-pt-0 tw-pt-[100px]">
                <div className="lg:tw-absolute tw-right-0 tw-top-0 tw-h-full lg:tw-w-2/3 tw-overflow-hidden lg:tw-mt-0 tw-mt-[100px]">
                    <video
                        muted
                        autoPlay
                        poster={poster ? getImage(poster).images.fallback.src : null}
                        loop
                        className="tw-min-h-full tw-min-w-full lg:tw-object-cover lg:tw-object-center tw-cursor-pointer"
                        onClick={() => {
                            if (videoViewTrackingEvent) {
                                trackEvent(videoViewTrackingEvent);
                            }
                            setShowFullScreenVideo(true);
                        }}
                    >
                        <source src={videoUrl} type="video/mp4" />
                        <p>
                            Your browser doesn't support HTML5 video. Here is a
                            <a href={videoUrl}>link to the video</a> instead.
                        </p>
                    </video>
                </div>
                <div
                    id={`play - ${title}`}
                    className="playButton lg:tw-flex tw-items-end tw-justify-end tw-hidden tw-absolute tw-right-0 tw-top-0 tw-h-full lg:tw-w-2/3 tw-overflow-hidden tw-z-10 tw-group tw-p-64 tw-cursor-pointer"
                    onClick={() => {
                        if (videoViewTrackingEvent) {
                            trackEvent(videoViewTrackingEvent, {
                                click_source: VideoPlayClickSource.HOMEPAGE_HERO_VIDEO_OVERLAY
                            });
                        }
                        setShowFullScreenVideo(true);
                    }}
                >
                    <div className="group-hover:tw-opacity-100 tw-opacity-0 tw-transition-opacity tw-flex tw-items-center tw-text-neutral-inverse tw-gap-24 tw-text-lg">
                        Click to watch video
                        <div className="tw-w-[60px] tw-h-[60px]">
                            <PlayButtonSVG />
                        </div>
                    </div>
                </div>
                <div className="lg:videoGradientToLeft tw-w-full tw-h-full tw-relative">
                    <div className="container tw-h-full">
                        <div className="tw-flex lg:tw-items-center lg:tw-justify-start tw-justify-center lg:tw-text-left tw-h-full">
                            <div>
                                <div className="lg:tw-prose-xl tw-prose tw-text-neutral-dark-subdued">
                                    {title ? (
                                        <span className="tw-text-neutral-dark-subdued tw-text-2xl tw-tracking-wide">
                                            {title}
                                        </span>
                                    ) : null}
                                    <h1 className="tw-text-neutral-inverse tw-my-24 lg:tw-text-4xl tw-font-bold">
                                        {header}
                                    </h1>
                                    {paragraph ? (
                                        <p className="tw-max-w-[700px]">{paragraph}</p>
                                    ) : null}
                                </div>

                                <div className="tw-mt-48 tw-inline-block tw-relative tw-z-20">
                                    {cta ? (
                                        cta
                                    ) : (
                                        <PlayButton
                                            text={playButtonText ?? 'See how to get to goal'}
                                            callback={() => {
                                                if (videoViewTrackingEvent) {
                                                    trackEvent(videoViewTrackingEvent, {
                                                        click_source:
                                                            VideoPlayClickSource.HOMEPAGE_HERO_BUTTON
                                                    });
                                                }
                                                setShowFullScreenVideo(true);
                                            }}
                                            onDarkBackground={true}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
